import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { usePublicData } from '../../context/PublicDataContext';
import { img, Eyebrow, Display, PrimaryButton, Skeleton } from './components/ui';
import Seo from './components/Seo';

/* Renders any published page from website_pages by slug (GET
 * /website/public/pages/:slug). Content is admin-authored HTML from the CMS.
 * Missing/unpublished slugs get a graceful, on-brand fallback instead of a crash. */
export default function PublicPage() {
  const { slug } = useParams();
  const { siteName } = usePublicData();
  const [page, setPage] = useState(undefined); // undefined = loading, null = not found
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setPage(undefined);
    setError(false);
    (async () => {
      try {
        const res = await api.get(`/website/public/pages/${slug}`);
        if (alive) setPage(res.data || null);
      } catch (err) {
        if (!alive) return;
        if (err?.response?.status === 404) setPage(null);
        else { setError(true); setPage(null); }
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (page === undefined) {
    return (
      <div className="max-w-3xl mx-auto px-6 pt-40 pb-32">
        <Skeleton className="h-4 w-28 mb-6" />
        <Skeleton className="h-12 w-3/4 mb-8" />
        <Skeleton className="h-4 w-full mb-3" />
        <Skeleton className="h-4 w-full mb-3" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (!page) {
    return (
      <>
        <Seo title={prettySlug(slug)} description={`${prettySlug(slug)} — ${siteName}`} siteName={siteName} />
        <div className="max-w-2xl mx-auto px-6 pt-40 pb-32 text-center">
          <Eyebrow>{prettySlug(slug)}</Eyebrow>
          <Display as="h1" className="text-3xl md:text-4xl mt-5 mb-4">This page isn't published yet</Display>
          <p className="text-[#4a3d32] mb-8">
            {error
              ? 'We couldn’t load this page right now. Please try again shortly.'
              : `The “${prettySlug(slug)}” page can be created and published from the Website section of the admin panel.`}
          </p>
          <PrimaryButton to="/">Back to home</PrimaryButton>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title={page.meta_title || page.title}
        description={page.meta_description || undefined}
        image={page.og_image ? img(page.og_image, undefined) : undefined}
        type="article"
        siteName={siteName}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
            { '@type': 'ListItem', position: 2, name: page.title },
          ],
        }}
      />

      {page.og_image ? (
        <section className="relative pt-44 pb-20 text-[#f4ece0]">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${img(page.og_image)})` }} />
          <div className="absolute inset-0 bg-[#160f0aa6]" />
          <div className="relative max-w-4xl mx-auto px-6">
            <Eyebrow tone="light">{siteName}</Eyebrow>
            <Display as="h1" className="text-4xl md:text-[52px] mt-4">{page.title}</Display>
          </div>
        </section>
      ) : (
        <section className="pt-40 pb-10">
          <div className="max-w-4xl mx-auto px-6">
            <nav className="text-[12px] text-[#4a3d32] mb-6"><Link to="/" className="hover:text-[#b5622f]">Home</Link> / {page.title}</nav>
            <Display as="h1" className="text-4xl md:text-[52px]">{page.title}</Display>
          </div>
        </section>
      )}

      <article className="max-w-3xl mx-auto px-6 py-14">
        <div
          className="moji-prose text-[16px] leading-[1.85] text-[#3a2f26]"
          dangerouslySetInnerHTML={{ __html: page.content || '' }}
        />
      </article>

      <style>{`
        .moji-prose h2 { font-family:'Fraunces',serif; font-size:1.6rem; margin:2.2rem 0 .8rem; color:#241811; }
        .moji-prose h3 { font-family:'Fraunces',serif; font-size:1.25rem; margin:1.8rem 0 .6rem; color:#241811; }
        .moji-prose p { margin:0 0 1.1rem; }
        .moji-prose a { color:#b5622f; text-decoration:underline; text-underline-offset:2px; }
        .moji-prose ul,.moji-prose ol { margin:0 0 1.1rem 1.2rem; }
        .moji-prose li { margin:.3rem 0; }
        .moji-prose img { max-width:100%; height:auto; margin:1.6rem 0; }
        .moji-prose blockquote { border-left:3px solid #b5622f; padding-left:1rem; font-style:italic; color:#4a3d32; margin:1.4rem 0; }
      `}</style>
    </>
  );
}

function prettySlug(slug = '') {
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
